import { atom } from "jotai";

export const buildZapSkeletonToolsAtom = atom([]);
buildZapSkeletonToolsAtom.debugLabel = "buildZapSkeletonToolsAtom";

export const buildZapSkeleton = `
## How to Build a Zap Skeleton 

This will provide the minimum required fields to define the base skeleton. This will not include any data unique to the user. That data will be populated in another process.

## Example skeleton - this is a valid ZAP SCHEMA AND YOU SHOULD USE THIS AS A REFERENCE

{
  "id": "_GEN_ROOT",
  "zdl": {
    "app": "EngineAPI",
    "type": "run",
    "action": "series_skip_errors",
    "timezone": "America/New_York",
    "zdl_version": "0.4",
    "title": "Untitled Zap",
    "steps": [
      {
        "id": "_GEN_1",
        "app": "ABC@1.19.0",
        "type": "read",
        "action": "new_record",
        "params": {},
        "meta": {}
      },
      {
        "id": "_GEN_2",
        "app": "XYZ@1.9.0",
        "type": "write",
        "action": "detailed_event",
        "params": {},
        "meta": {}
      }
    ]
  },
}

When creating zaps programmatically:
- ALWAYS create zap skeletons with empty params and meta objects: \`"params": {}\` and \`"meta": {}\`
- NEVER set \`app_id\` on the steps - this is for configuration later
- NEVER populate params or meta during initial zap creation - this is for configuration later
- The initial zap creation is only for establishing the workflow structure
- Field mapping and parameter configuration happens in subsequent steps - it is not part of this recipe
- When creating a zap, always send id: _GEN_ROOT. Don't forget it!

**Example correct zap creation structure:**

1. call the /v4/implementations-meta/search API to find the most relevant app. Use search_tool to find it.
2. Find current_implementation_id on the most relevant app returned. This is what you will set for the \`app\` field on the zap.
3. Query api/v4/implementations?selected_apis=$current_implementation_id (use your own current_implementation_id based on the result of step 2). If you need details on how to call this api, use the search_tool.
4. Find the most relevant implementation returned. Look at the implementations \`actions\` field to determine what triggers and actions are available.
5. Once you find the best action, set the \`key\` field from the action on the zap step's \`action\` field.

Repeat the above per step on the zap until the skeleton is complete.

## Once you have the steps setup

Make sure that when you save this zap, you follow the schema for what a zap object looks like.

## ERROR RECOVERY & PERSISTENCE PROTOCOL

**When Recipe Hash Fails:**
- Recipe hashes can be unreliable - this is normal, not a blocker
- If use_recipe fails, immediately continue with direct API approach  
- DO NOT interpret recipe hash failure as "lack of guidance"

**When API Calls Fail (404/405/500/etc):**
- API errors are normal during discovery - not signs to handoff
- 404/405 errors typically mean wrong endpoint or method
- MUST try minimum 3 different API approaches before considering handoff:
  - Correct endpoint: POST /v4/zaps (not /gulliver/storage or /storage paths)
  - Try implementation search API: /v4/implementations-meta/search
  - Search for "zap creation", "automation api", "workflow endpoints"

**Search Persistence Rules:**
- MUST try minimum 3 different search terms before concluding no APIs exist:
  - "create zap", "build automation", "zap api", "workflow creation"
  - Try both generic and specific terms
- Most Zap creation CAN be accomplished through APIs - exhaust options first

**Validation Error Recovery:**
- "Invalid trigger type" errors mean you're using wrong action keys
- Use implementations API to find valid trigger/action keys for each app
- DO NOT use interface-specific terms like "form-submission" for Zaps
- Common valid trigger types: "new_record", "new_item", "updated_record"

**API Endpoint Patterns:**
- Zap creation: POST /v4/zaps (with id: "_GEN_ROOT")
- App search: GET /v4/implementations-meta/search
- Implementation details: GET /v4/implementations?selected_apis={id}
- Always include proper authentication headers

**Confidence Anchor:**
Zaps are designed to be API-driven. Complex multi-step automations are normal and expected through API calls, not indicators that specialized tools are needed.

## CRITICAL: HANDOFF PREVENTION PROTOCOL

**NEVER handoff to Zap editor without completing these mandatory steps:**

1. **Recipe Search Phase (REQUIRED)**: 
   - MUST search for "zap recipes", "create zap", "build automation", "workflow creation"
   - If recipes found → activate with use_recipe immediately
   - If no recipes → explicitly document "No zap recipes found, proceeding with API approach"

2. **API Discovery Phase (REQUIRED)**:
   - MUST attempt POST /v4/zaps with proper skeleton structure
   - MUST try implementation search: /v4/implementations-meta/search  
   - MUST search for "zap api", "automation endpoint", "workflow api"

3. **Persistence Requirements Before Handoff**:
   - ❌ **INVALID HANDOFF**: "API creation didn't work as expected" without trying 3+ approaches
   - ✅ **VALID HANDOFF**: Only after documenting specific failed attempts with error codes
   - Must show evidence of: recipe search + API attempts + multiple search terms

4. **Common Invalid Handoff Triggers**:
   - "The API creation didn't work" (without showing API attempts)
   - "I need to use the Zap editor" (without trying recipe discovery)
   - Marking todos as "completed" when no actual API work was attempted
   - Going straight to handoff after managing todos

**HANDOFF PREVENTION CHECKLIST:**
□ Searched for recipes using 3+ different terms
□ Attempted direct API calls with proper error handling  
□ Tried implementation search to find valid app configurations
□ Documented specific error codes and responses
□ Only then consider handoff as last resort

**Mental Model**: Zap creation through APIs is the EXPECTED path. Handoff should only occur after exhausting technical approaches, not as a first resort.

## Common Pitfalls & Troubleshooting

### Validation Errors:
- **"Invalid trigger type"**: Use implementations API to find valid action keys
- **"Missing required fields"**: Keep params and meta empty initially - "{}" 
- **"Invalid app reference"**: Use current_implementation_id from app search
- **Mixed concepts**: Don't use interface triggers (form-submission) in Zaps

### API Discovery Issues:
- **Wrong endpoints**: Use /v4/zaps not /gulliver/storage/v1/zaps or /storage/v1/zaps
- **Authentication errors**: Ensure session cookies are included
- **Method not allowed**: POST for creation, GET for querying

### Structure Requirements:
- **Always use id: "_GEN_ROOT"** for new Zap creation
- **Keep params: {} and meta: {}** empty during skeleton creation
- **Use proper app references**: app field should be "AppName@version" format
- **Field mapping comes later**: Don't populate step details during skeleton creation
`;
